import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

export interface SupportBundleData {
  bundleId: string
  hostIds: string[]
}

export type BundleStatus =
  | 'pending'
  | 'in_progress'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'partial_success'

export interface BundleState {
  bundleId: string
  hostIds: string[]
  overallStatus: BundleStatus
  hostCount: number
  completedCount?: number
  failedCount?: number
  inProgressCount?: number
  cancelledCount?: number
  error?: string | null
  downloading: boolean
  autoDownloadTriggered: boolean
  downloadError?: string | null
  createdAt?: string
  updatedAt?: string
  metadata?: {
    label?: string | null
    upload?: boolean
  }
  hostStatuses?: Record<
    string,
    {
      status: string
      bundleUuid?: string
      error?: string | null
      updatedAt?: string
    }
  >
}

interface SupportBundleContextValue {
  bundleStates: Record<string, BundleState>
  bundles: SupportBundleData[] // Computed from bundleStates for backward compatibility
  showSupportBundlePopover: (data: SupportBundleData) => void
  updateBundleStatus: (bundleId: string, status: Partial<BundleState>) => void
  setBundleDownloading: (bundleId: string, downloading: boolean) => void
  setBundleDownloadError: (bundleId: string, error: string | null) => void
  setAutoDownloadTriggered: (bundleId: string) => void
  removeBundle: (bundleId: string) => void
  closeAllBundles: () => void
}

const SupportBundleProgressContext = createContext<SupportBundleContextValue | null>(null)

export const useSupportBundleProgress = () => {
  const context = useContext(SupportBundleProgressContext)
  if (!context) {
    throw new Error('useSupportBundleProgress must be used within a SupportBundleProgressProvider')
  }
  return context
}

export const SupportBundleProgressProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [bundleStates, setBundleStates] = useState<Record<string, BundleState>>({})

  // Compute bundles array from bundleStates for backward compatibility
  const bundles: SupportBundleData[] = useMemo(
    () =>
      Object.values(bundleStates).map((state) => ({
        bundleId: state.bundleId,
        hostIds: state.hostIds,
      })),
    [bundleStates],
  )

  const showSupportBundlePopover = useCallback((data: SupportBundleData) => {
    setBundleStates((prev) => {
      if (prev[data.bundleId]) {
        return prev // Already exists
      }
      const newBundle: BundleState = {
        bundleId: data.bundleId,
        hostIds: data.hostIds,
        overallStatus: 'pending' as BundleStatus,
        hostCount: data.hostIds.length,
        downloading: false,
        autoDownloadTriggered: false,
        downloadError: null,
      }
      return {
        ...prev,
        [data.bundleId]: newBundle,
      }
    })
  }, [])

  const updateBundleStatus = useCallback((bundleId: string, status: Partial<BundleState>) => {
    setBundleStates((prev) => {
      const existing = prev[bundleId]
      if (!existing) return prev
      return {
        ...prev,
        [bundleId]: {
          ...existing,
          ...status,
        } as BundleState,
      }
    })
  }, [])

  const setBundleDownloading = useCallback((bundleId: string, downloading: boolean) => {
    setBundleStates((prev) => {
      const existing = prev[bundleId]
      if (!existing) return prev
      return {
        ...prev,
        [bundleId]: {
          ...existing,
          downloading,
          downloadError: downloading ? null : existing.downloadError,
        },
      }
    })
  }, [])

  const setBundleDownloadError = useCallback((bundleId: string, error: string | null) => {
    setBundleStates((prev) => {
      const existing = prev[bundleId]
      if (!existing) return prev
      return {
        ...prev,
        [bundleId]: {
          ...existing,
          downloadError: error,
        },
      }
    })
  }, [])

  const setAutoDownloadTriggered = useCallback((bundleId: string) => {
    setBundleStates((prev) => {
      const existing = prev[bundleId]
      if (!existing) return prev
      return {
        ...prev,
        [bundleId]: {
          ...existing,
          autoDownloadTriggered: true,
        },
      }
    })
  }, [])

  const removeBundle = useCallback((bundleId: string) => {
    setBundleStates((prev) => {
      const newState = { ...prev }
      delete newState[bundleId]
      return newState
    })
  }, [])

  const closeAllBundles = useCallback(() => {
    setBundleStates({})
  }, [])

  return (
    <SupportBundleProgressContext.Provider
      value={{
        bundleStates,
        bundles,
        showSupportBundlePopover,
        updateBundleStatus,
        setBundleDownloading,
        setBundleDownloadError,
        setAutoDownloadTriggered,
        removeBundle,
        closeAllBundles,
      }}
    >
      {children}
    </SupportBundleProgressContext.Provider>
  )
}
